/*
 * Mini-cart mobile : panneau, carrousel, présélection par stock, verre au scroll.
 * L'animation d'ajout est celle du thème (.button.loading). Le desktop reste géré par AnimateSticky (global.js).
 * product-info.js recharge variant-selects[data-sticky] à chaque changement de variante.
 */
if (!customElements.get('sticky-cart-controller')) {
  const EASE = 'cubic-bezier(.16, 1, .3, 1)';
  const GLASS_AFTER_DOWN = 24;
  const WHITE_AFTER_UP = 12;
  /* Certaines fiches remplacent tout <main> au changement d'option (product-info.js) :
     le contrôleur est alors recréé, ce qui doit survivre est gardé ici. */
  const memory = { open: false, manualSize: false, trackScroll: 0 };

  customElements.define(
    'sticky-cart-controller',
    class StickyCartController extends HTMLElement {
      connectedCallback() {
        this.root = this.closest('animate-sticky');
        if (!this.root) return;

        this.mobile = window.matchMedia('(max-width: 750px)');
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.cart = this.root.querySelector('.sticky-cart');
        this.panel = this.root.querySelector('.sticky-panel__inner');
        this.opener = this.root.querySelector('[data-sticky-open]');
        this.productForm = this.root.querySelector('product-form');
        this.atc = this.root.querySelector('[data-sticky-atc]');
        this.atcLabel = this.root.querySelector('[data-sticky-atc-label]');
        this.idInput = this.productForm.querySelector('[name="id"]');
        this.baseLabel = this.atcLabel.textContent.trim();
        this.lastY = window.scrollY;
        this.runDir = 0;
        this.run = 0;

        this.abort = new AbortController();
        const { signal } = this.abort;
        this.root.addEventListener('click', this.onClick.bind(this), { signal });
        this.root.addEventListener('change', this.onChange.bind(this), { capture: true, signal });
        document.addEventListener('keydown', this.onKeydown.bind(this), { signal });
        window.addEventListener('scroll', this.onScroll.bind(this), { passive: true, signal });
        window.addEventListener('resize', () => this.refreshPicker(false), { passive: true, signal });

        this.unsubscribers = [
          subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this)),
          subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this)),
          subscribe(PUB_SUB_EVENTS.cartError, this.onCartError.bind(this)),
        ];

        /* product-info.js remplace les sélecteurs : on rebranche le carrousel */
        if (this.panel) {
          this.pickerObserver = new MutationObserver(() => this.onPickerReplaced());
          this.pickerObserver.observe(this.panel, { childList: true });
        }

        this.preselectBestStock();
        this.refreshPicker(false);
        if (memory.open) this.openPanel(false);
      }

      disconnectedCallback() {
        this.abort?.abort();
        this.unsubscribers?.forEach((unsubscribe) => unsubscribe());
        this.pickerObserver?.disconnect();
        this.trackObserver?.disconnect();
      }

      /* Le nœud remplacé reste 500 ms en display: none : on vise toujours le nœud actif */
      get picker() {
        return this.root.querySelector('variant-selects[data-sticky]');
      }

      get stock() {
        const source = this.picker?.querySelector('[data-sticky-stock]');
        if (!source) return [];
        try {
          return JSON.parse(source.textContent);
        } catch (error) {
          return [];
        }
      }

      /* ---------- Présélection : variante dispo avec le plus de stock (interactive-card.js) ---------- */

      fieldsets() {
        return this.picker ? [...this.picker.querySelectorAll('[data-sticky-option]')] : [];
      }

      selectedValue(fieldset) {
        return fieldset.querySelector('input:checked')?.value ?? null;
      }

      bestVariant(colorIndex, colorValue) {
        return this.stock
          .filter((variant) => variant.available && (colorIndex === null || variant.options[colorIndex] === colorValue))
          .sort((a, b) => b.qty - a.qty)[0];
      }

      applyVariant(variant) {
        this.fieldsets().forEach((fieldset) => {
          const value = variant.options[Number(fieldset.dataset.stickyOption)];
          const input = [...fieldset.querySelectorAll('input[type="radio"]')].find((radio) => radio.value === value);
          if (input) input.checked = true;
        });
      }

      /* À l'arrivée sans ?variant= : même couleur que la galerie, taille la mieux stockée */
      preselectBestStock() {
        if (!this.mobile.matches || !this.picker) return;
        if (new URLSearchParams(window.location.search).has('variant')) return;

        const colorFieldset = this.picker.querySelector('[data-sticky-color]');
        const colorIndex = colorFieldset ? Number(colorFieldset.dataset.stickyOption) : null;
        const best = this.bestVariant(colorIndex, colorFieldset ? this.selectedValue(colorFieldset) : null);
        if (!best) return;

        this.applyVariant(best);
        this.idInput.value = best.id;
      }

      /* ---------- Événements ---------- */

      onClick(event) {
        if (!this.mobile.matches) return;
        if (event.target.closest('[data-sticky-open]')) this.openPanel(true);
        else if (event.target.closest('[data-sticky-close]')) this.closePanel();
        else if (event.target.closest('[data-sticky-more]')) {
          const track = this.picker.querySelector('[data-sticky-track]');
          track.scrollBy({ left: track.clientWidth * 0.8, behavior: this.reduced.matches ? 'auto' : 'smooth' });
        }
      }

      onKeydown(event) {
        if (event.key === 'Escape' && this.cart.classList.contains('is-open')) this.closePanel();
      }

      /* En capture : la taille est cochée avant que VariantSelects ne lise la sélection */
      onChange(event) {
        const input = event.target;
        if (!this.mobile.matches || !input.closest?.('variant-selects[data-sticky]')) return;

        const fieldset = input.closest('[data-sticky-option]');
        if (fieldset?.hasAttribute('data-sticky-color')) {
          this.onColorPicked(fieldset, input);
          this.centerSwatch(input);
        } else {
          memory.manualSize = true;
        }

        this.updateSelLabel();
        this.atc.setAttribute('aria-disabled', 'true');
      }

      /* Taille gardée si elle existe dans la nouvelle couleur et a été choisie à la main, sinon plus gros stock */
      onColorPicked(fieldset, input) {
        const colorIndex = Number(fieldset.dataset.stickyOption);
        const others = this.fieldsets().filter((other) => other !== fieldset);
        const keep =
          memory.manualSize &&
          this.stock.some(
            (variant) =>
              variant.available &&
              variant.options[colorIndex] === input.value &&
              others.every(
                (other) => variant.options[Number(other.dataset.stickyOption)] === this.selectedValue(other)
              )
          );
        if (keep) return;

        const best = this.bestVariant(colorIndex, input.value);
        if (best) this.applyVariant(best);
        memory.manualSize = false;
      }

      onPickerReplaced() {
        this.refreshPicker(true);
        /* updateVariantInputs vide l'id si la combinaison n'existe pas */
        if (this.idInput.value === '') {
          this.setAtcState(true, window.theme?.variantStrings?.unavailable ?? this.baseLabel);
        }
      }

      onVariantChange({ data: { html } }) {
        const ship = html.querySelector('[data-sticky-sync="ship"]');
        const currentShip = this.root.querySelector('[data-sticky-sync="ship"]');
        if (ship && currentShip) currentShip.innerHTML = ship.innerHTML;

        const nextAtc = html.querySelector('[data-sticky-atc]');
        if (nextAtc) {
          const label = nextAtc.querySelector('[data-sticky-atc-label]').textContent.trim();
          this.setAtcState(nextAtc.hasAttribute('disabled'), label);
        }
      }

      setAtcState(disabled, label) {
        this.baseLabel = label;
        this.atcLabel.textContent = label;
        this.atc.toggleAttribute('disabled', disabled);
        this.atc.removeAttribute('aria-disabled');
      }

      /* ---------- Fond : blanc au repos, verre après 24 px vers le bas, blanc après 12 px vers le haut ---------- */

      onScroll() {
        if (!this.mobile.matches) return;
        const y = window.scrollY;
        const delta = y - this.lastY;
        this.lastY = y;

        if (y <= 4) {
          this.cart.classList.remove('is-glass');
          this.run = 0;
          this.runDir = 0;
          return;
        }
        if (delta === 0) return;

        const direction = Math.sign(delta);
        if (direction !== this.runDir) {
          this.runDir = direction;
          this.run = 0;
        }
        this.run += Math.abs(delta);

        if (direction > 0 && this.run >= GLASS_AFTER_DOWN) this.cart.classList.add('is-glass');
        if (direction < 0 && this.run >= WHITE_AFTER_UP) this.cart.classList.remove('is-glass');
      }

      /* ---------- Panneau ---------- */

      openPanel(animate) {
        if (!this.panel || !this.opener) return;
        const swatchFrom = this.opener.querySelector('[data-sticky-sel-swatch] .swatch');

        memory.open = true;
        this.cart.classList.add('is-open');
        this.root.classList.add('is-panel-open');
        this.opener.setAttribute('aria-expanded', 'true');
        this.refreshPicker(false);

        const checked = this.picker?.querySelector('[data-sticky-color] input:checked');
        if (checked) this.centerSwatch(checked, true);

        if (animate && swatchFrom && checked && !this.reduced.matches) this.flySwatch(swatchFrom, checked);
        this.panel.focus({ preventScroll: true });
      }

      closePanel() {
        memory.open = false;
        this.cart.classList.remove('is-open');
        this.root.classList.remove('is-panel-open');
        this.opener?.setAttribute('aria-expanded', 'false');
        if (this.panel?.contains(document.activeElement)) this.opener?.focus({ preventScroll: true });
      }

      /* L'ovale de la barre rejoint sa place dans le carrousel */
      flySwatch(from, input) {
        const target = input.nextElementSibling?.querySelector('.swatch');
        if (!target) return;

        const cartRect = this.cart.getBoundingClientRect();
        const start = from.getBoundingClientRect();
        const end = target.getBoundingClientRect();
        const panelShift = new DOMMatrix(getComputedStyle(this.panel).transform).m42;

        const wrapper = document.createElement('span');
        wrapper.className = 'sticky-flyer product-form__input';
        wrapper.style.left = `${start.left - cartRect.left}px`;
        wrapper.style.top = `${start.top - cartRect.top}px`;
        wrapper.append(from.cloneNode(true));
        this.cart.append(wrapper);

        target.style.visibility = 'hidden';
        wrapper.animate(
          [{ transform: 'none' }, { transform: `translate(${end.left - start.left}px, ${end.top - panelShift - start.top}px)` }],
          { duration: 380, easing: EASE }
        ).onfinish = () => {
          wrapper.remove();
          target.style.visibility = '';
        };
      }

      /* ---------- Carrousel et cadre ---------- */

      refreshPicker(scrollToSelection) {
        const track = this.picker?.querySelector('[data-sticky-track]');
        if (track && track !== this.track) {
          this.track = track;
          track.scrollLeft = memory.trackScroll;
          track.addEventListener('scroll', () => {
            memory.trackScroll = track.scrollLeft;
            this.updateMore();
          }, { passive: true });
          this.trackObserver?.disconnect();
          this.trackObserver = new ResizeObserver(() => this.updateMore());
          this.trackObserver.observe(track);
          const checked = track.querySelector('input:checked');
          if (checked && scrollToSelection) this.centerSwatch(checked);
        }
        this.updateMore();
        this.updateSelLabel();
      }

      /* Compteur +N : initSwatchScroller d'interactive-card.js */
      updateMore() {
        const track = this.track;
        const more = this.picker?.querySelector('[data-sticky-more]');
        if (!track || !more) return;

        const swatches = track.querySelectorAll('input');
        if (!swatches.length || !track.clientWidth) {
          more.hidden = true;
          return;
        }
        const unit = track.scrollWidth / swatches.length;
        const remaining = track.scrollWidth - track.clientWidth - track.scrollLeft;
        if (remaining < unit * 0.5) {
          more.hidden = true;
          return;
        }
        more.querySelector('[data-sticky-more-count]').textContent = `+${Math.max(Math.round(remaining / unit), 1)}`;
        more.hidden = false;
      }

      centerSwatch(input, instant = false) {
        const label = input.nextElementSibling;
        const track = input.closest('[data-sticky-track]');
        if (!label || !track) return;
        const left = label.offsetLeft - track.offsetLeft - track.clientWidth / 2 + label.offsetWidth / 2;
        track.scrollTo({ left, behavior: instant || this.reduced.matches ? 'auto' : 'smooth' });
      }

      /* Valeurs et pastille de la barre, mises à jour sans attendre le serveur */
      updateSelLabel() {
        const values = this.root.querySelector('[data-sticky-values]');
        if (!values || !this.picker) return;

        const selected = this.fieldsets().map((fieldset) => this.selectedValue(fieldset)).filter(Boolean);
        if (selected.length) values.textContent = selected.join(' · ');

        const target = this.picker.querySelector('[data-sticky-color] input:checked + label .swatch');
        const swatch = this.root.querySelector('[data-sticky-sel-swatch] .swatch');
        if (target && swatch) {
          swatch.className = target.className;
          swatch.setAttribute('style', target.getAttribute('style') ?? '');
        }

        if (this.opener) this.opener.setAttribute('aria-label', `${this.opener.querySelector('small').textContent} : ${selected.join(', ')}`);
      }

      /* ---------- Ajout au panier ---------- */

      /* Le tiroir recouvre le module : le panneau se referme derrière lui */
      onCartUpdate() {
        if (this.mobile.matches) this.closePanel();
      }

      /* Le message d'erreur est dans le formulaire : le panneau s'ouvre pour le contexte */
      onCartError() {
        if (this.mobile.matches && this.panel && !this.cart.classList.contains('is-open')) this.openPanel(false);
      }
    }
  );
}
